import {
  Calendar01Icon,
  CheckListIcon,
  CheckUnread03Icon,
  TextIcon,
  TextNumberSignIcon,
} from "@hugeicons/core-free-icons/index";
import { HugeiconsIcon } from "@hugeicons/react";
import { Badge, MenuItem, Stack, type MenuItemProps } from "@mui/material";
import { useMuiMenu } from "@shared/frontend/hooks/use-mui-menu.hook";
import React from "react";
import { useHyperTable } from "../../HyperTable";
import type {
  HyperTableCustomFilter,
  HyperTableEntityFilter,
  HyperTableFilter,
  HyperTableFilterBoth,
} from "../../types";
import { FilterMenuInnerMenu } from "./FilterMenuInnerMenu";
import {
  isFieldBoolean,
  isFieldDate,
  isFieldLiteral,
  isFieldNumber,
} from "./filter-types/helpers";

interface FilterMenuItemProps<Entity>
  extends Omit<MenuItemProps, "children" | "ref"> {
  filter: HyperTableFilter<Entity>;
}

export function isCustomFilter<Entity>(
  filter: HyperTableFilter<Entity>,
): filter is HyperTableCustomFilter<Entity> & HyperTableFilterBoth<Entity> {
  return "renderFilter" in filter;
}

export function isEntityFilter<Entity>(
  filter: HyperTableFilter<Entity>,
): filter is HyperTableEntityFilter<Entity> & HyperTableFilterBoth<Entity> {
  return !("renderFilter" in filter);
}

export const FilterMenuItem = <Entity extends any>(
  props: FilterMenuItemProps<Entity>,
) => {
  const { filter, ...menuItemProps } = props;

  const menu = useMuiMenu<HTMLLIElement>();
  const [ref, , menuActions] = menu;

  const { filterActions: activeFilterActions, getEntity } =
    useHyperTable<Entity>();

  return (
    <MenuItem
      key={filter.id}
      ref={ref}
      onClick={() => {
        if (!activeFilterActions.has(filter.id))
          activeFilterActions.add({
            id: filter.id,
            value: {},
            meta: {},
            saveInSessionStorage: filter.saveInSessionStorage ?? true,
          });
        menuActions.toggle();
      }}
      {...menuItemProps}
    >
      <Stack direction="row" gap="0.8rem" alignItems="center">
        {isCustomFilter(filter) &&
          (filter.icon === "literal" ? (
            <HugeiconsIcon icon={CheckListIcon} size={18} />
          ) : filter.icon === "date" ? (
            <HugeiconsIcon icon={Calendar01Icon} size={19} />
          ) : filter.icon === "number" ? (
            <HugeiconsIcon icon={TextNumberSignIcon} size={16} />
          ) : filter.icon === "boolean" ? (
            <HugeiconsIcon icon={CheckUnread03Icon} size={19} />
          ) : filter.icon === "string" ? (
            <HugeiconsIcon icon={TextIcon} size={16} />
          ) : (
            filter.icon
          ))}
        {isEntityFilter(filter) &&
          (isFieldLiteral(getEntity(), filter.fieldName) ? (
            <HugeiconsIcon icon={CheckListIcon} size={18} />
          ) : isFieldDate(getEntity(), filter.fieldName) ? (
            <HugeiconsIcon icon={Calendar01Icon} size={19} />
          ) : isFieldNumber(getEntity(), filter.fieldName) ? (
            <HugeiconsIcon icon={TextNumberSignIcon} size={16} />
          ) : isFieldBoolean(getEntity(), filter.fieldName) ? (
            <HugeiconsIcon icon={CheckUnread03Icon} size={19} />
          ) : (
            <HugeiconsIcon icon={TextIcon} size={16} />
          ))}
        {filter.caption}
        <FilterMenuInnerMenu filterId={filter.id} menu={menu} />
      </Stack>
      <Badge
        color="secondary"
        sx={{ marginRight: 10 }}
        componentsProps={{
          badge: {
            style: {
              width: "6px",
              height: "6px",
              left: -5,
              top: -5,
              minWidth: 0,
              padding: 0,
            },
          },
        }}
        badgeContent={activeFilterActions.has(filter.id) ? " " : 0}
      />
    </MenuItem>
  );
};
